/**
 * Inline chips in prose — the rendering half of ADR-127 D13.
 *
 * A stored artifact holds tokens (`{{data.properties.by_key.roofmaxx-com.wp_api_base}}`).
 * An operator should read a chip ("Website properties › roofmaxx-com › wp_api_base"). D13
 * requires that round-trip to be LOSSLESS in both directions, which is why ADR-126 D1
 * chose a verbose spelling in the first place — and why this module never invents a label:
 * it parses the token with the same grammar the validator uses, and falls back to showing
 * the raw token when it cannot.
 *
 * THE FALLBACK IS THE IMPORTANT PART. A chip renderer that swallowed an unparseable token
 * would hide a reference the operator needs to see and cannot then fix. Anything this
 * module does not recognise is passed through as literal text, visibly.
 */

/** The two grammars, mirrored from scripts/validate_data_refs.py. Mirrored rather than
 *  re-derived: these are the same two SPELLINGS, and the RULES live in Python (ADR-126
 *  D9). `dataReferenceChips.test.ts` pins them against the tokens the endpoint serves, so
 *  a grammar change cannot land here silently. */
const TEMPLATE_RE = /\{\{\s*data\.([A-Za-z0-9_-]+)\.([^}\s]+?)\s*\}\}/g
const EXPRESSION_RE = /(?<![\w.])\.data\.([A-Za-z0-9_-]+)\.([^\s"',)\]]+)/g

export interface ProseSegment {
  kind: 'text' | 'chip'
  /** For a chip: the label to render. For text: the literal content. */
  text: string
  /** Present on a chip — the exact token this chip stands for, so an edit can put it
   *  back byte-identically and a hover can show what will actually be substituted. */
  token?: string
  alias?: string
  path?: string
  syntax?: 'template' | 'expression'
}

/** Humanise an alias the way the generator's chip_label does: `website-properties` ->
 *  `Website properties`. Kept identical in spirit to the Python side; the test asserts the
 *  agreement against real served offers rather than trusting this comment. */
export function humaniseAlias(alias: string): string {
  const spaced = alias.replace(/[-_]/g, ' ').trim()
  return spaced.charAt(0).toUpperCase() + spaced.slice(1)
}

/**
 * A chip label for one reference — an EXACT mirror of `chip_label` in the canonical
 * generator:
 *
 *     humanised alias › each dot-separated segment of the path
 *
 * Deliberately as dumb as the Python. My first version special-cased `by_key`, dropping
 * the accessor from the literal form and rendering the bracketed one "readably" — and the
 * round-trip test against real served offers caught it immediately: the generator emits
 * "Website properties › by_key › roofmaxx-com › key" and this produced
 * "Website properties › roofmaxx-com › key". A cleverer mirror is a different mirror, and
 * a chip the operator sees that the validator never emitted is the ADR-121 failure mode in
 * miniature. If the label should read better, it changes in the Python and both follow.
 *
 * Note the bracketed operand needs no special case at all: `by_key[inputs.property].key`
 * split on "." yields `by_key[inputs`, `property]`, `key` — which is exactly the
 * "by_key[inputs › property] › key" the generator produces, for the same reason.
 */
export function chipLabel(alias: string, path: string): string {
  const human = humaniseAlias(alias) || alias
  const segments = path.split('.').filter((p) => p.length > 0)
  return `${human} › ${segments.join(' › ')}`
}

/**
 * Split prose into text and chip segments, in order, losslessly.
 *
 * Concatenating every segment's `token ?? text` must reproduce the input exactly — that is
 * the property `dataReferenceChips.test.ts` asserts, and it is what makes it safe to
 * render chips in an editable field: whatever the operator did not touch goes back
 * unchanged.
 */
export function splitProse(prose: string): ProseSegment[] {
  if (!prose) return []
  type Hit = { start: number; end: number; alias: string; path: string
               syntax: 'template' | 'expression' }
  const hits: Hit[] = []

  for (const [re, syntax] of [[TEMPLATE_RE, 'template'], [EXPRESSION_RE, 'expression']] as const) {
    re.lastIndex = 0
    let m: RegExpExecArray | null
    while ((m = re.exec(prose)) !== null) {
      hits.push({ start: m.index, end: m.index + m[0].length, alias: m[1], path: m[2], syntax })
    }
  }
  // Earliest first; on a tie the longer match wins, so a template token is never
  // shadowed by an expression match starting inside it.
  hits.sort((a, b) => a.start - b.start || (b.end - b.start) - (a.end - a.start))

  const segments: ProseSegment[] = []
  let cursor = 0
  for (const hit of hits) {
    if (hit.start < cursor) continue // overlapping — the outer match already consumed it
    if (hit.start > cursor) {
      segments.push({ kind: 'text', text: prose.slice(cursor, hit.start) })
    }
    segments.push({
      kind: 'chip',
      text: chipLabel(hit.alias, hit.path),
      token: prose.slice(hit.start, hit.end),
      alias: hit.alias,
      path: hit.path,
      syntax: hit.syntax,
    })
    cursor = hit.end
  }
  if (cursor < prose.length) {
    segments.push({ kind: 'text', text: prose.slice(cursor) })
  }
  return segments
}

/** The inverse: segments back to storable prose. Lossless by construction — a chip
 *  contributes its TOKEN, never its label. */
export function joinProse(segments: ProseSegment[]): string {
  return segments.map((s) => (s.kind === 'chip' ? (s.token ?? s.text) : s.text)).join('')
}

/**
 * Insert a token at a caret position, without disturbing anything else.
 *
 * Returned separately from the component so the behaviour is testable without rendering:
 * the caret arithmetic is where an editor silently eats a character.
 */
export function insertToken(prose: string, caret: number, token: string):
  { prose: string; caret: number } {
  const at = Math.max(0, Math.min(caret, prose.length))
  return { prose: prose.slice(0, at) + token + prose.slice(at), caret: at + token.length }
}
